<template>
  <div class="wt-container">
            <p class="introduction-text text-center">
                The {{ globalText }} annual {{ assessment.viewColor }} water footprint{{ inOfText }}, per {{ perText }}.
            </p>
            <CountryOrBasinSelector class="mb-3"/>
                <div class="row">
                    <label for="threshold" class="col-auto col-form-label">Threshold</label>
                    <div class="col-auto">
                        <div class="input-group">
                            <input type="number" v-model="threshold" min="1" max="10" step="1" id="threshold" class="form-control">
                            <span class="input-group-text">%</span>
                        </div>
                    </div>
                </div>
                <div class="form-text">
                    All {{ belowText }} below the given percentage<br/>
                    <span v-if="containsOther">are included in the </span><span v-else>will be moved to a </span>group "Other"
                </div>
            <PieChart id="pie-chart" :color="assessment.viewColor" :data="data" :attrName="attributeName" :innerRadius="innerRadius" style="min-height: 500px"></PieChart>
  </div>
</template>

<script>
import { assessment } from "@/services/assessment-model.js"
import CountryOrBasinSelector from "@/components/shared/CountryOrBasinSelector.vue"
import { countryCropsByCountry, basinCropsByBasin, getCrop, scenarioCountryCrops, scenarioCrops, worldCrops } from "@/services/entity-model.js"
import PieChart from "@/components/shared/PieChart.vue"
import * as _ from 'lodash'

export default {
    name: "PieChartCt",
    data() {
        return {
            assessment: assessment,
            countryCropsByCountry: countryCropsByCountry,
            basinCropsByBasin: basinCropsByBasin,
            scenarioCountryCrops: scenarioCountryCrops,
            scenarioCrops: scenarioCrops,
            threshold: 5
        }
    },
    components: {
        PieChart,
        CountryOrBasinSelector
    },
    computed: {
        geographic() {
            return assessment.isGeographic() || (assessment.assessmentType.name == "hbasins");
        },
        crops() {
          if (this.geographic) {
            if (assessment.assessmentType.name == "countries") {
                return this.countryCropsByCountry;
            }
            if (assessment.assessmentType.name == "basins") {
                return this.basinCropsByBasin;
            }
            return worldCrops;
          }
          if (this.assessment.countriesOrBasins.length == 0) {
            return this.scenarioCrops.filter(scenarioCrop => (scenarioCrop.cropLevel == 4) || [1009, -2].includes(scenarioCrop.cropID));
          }
          return this.scenarioCountryCrops.filter(scenarioCountryCrop => scenarioCountryCrop.countryId == this.assessment.countriesOrBasins[0].countryID);
        },
        cropsWithValues() {
            return this.crops.filter(o => o[this.attributeName.value] > 0);
        },
        globalText() {
            if ((assessment.assessmentType.name == "world") || (assessment.assessmentType.name == "hbasins")) {
                return "global";
            }
            return "";
        },
    inOfText() {
      if (this.geographic) {
        if ((assessment.assessmentType.name == "world") || (assessment.assessmentType.name == "hbasins")) {
                return "";
            }
            return " in " + assessment.getCountryOrBasinName();
        }
        return " of your commodity, " + assessment.scenario.name + (!assessment.countriesOrBasins.length ? "" : ", in the selected country");
        },
    perText() {
      if (this.geographic) {
        return "crop or sector";
      }
      return "crop or material";
    },
    belowText() {
      if (this.geographic) {
        return "crops and sectors";
      }
      return "crops and materials";
    },
        data: function() {
            const crops = this.cropsWithValues;
            const filteredCrops = crops.filter((o) => o[this.attributeName.percent] >= this.threshold / 100);
            const otherCrops = crops.filter((o) => o[this.attributeName.percent] < this.threshold / 100);
            const data = filteredCrops.map(crop => {
                return {
                    "cropName": getCrop(crop.cropId || crop.cropID)?.name || "Locations",
                    [this.attributeName.percent]: crop[this.attributeName.percent], 
                    [this.attributeName.value]: parseFloat(crop[this.attributeName.value].toPrecision(2)),
                }
            });

            const otherWF = _.sumBy(otherCrops, this.attributeName.value);
            if (otherWF > 0) {
                const other = { cropName: "Other" };
                other[this.attributeName.percent] = _.sumBy(otherCrops, this.attributeName.percent);
                other[this.attributeName.value] = parseFloat(otherWF.toPrecision(2));
                data.push(other);
            } else if (data.length == 0) {
                const noData = { cropName: "No data available" };
                noData[this.attributeName.percent] = 1;
                noData[this.attributeName.value] = 0;
                data.push(noData);
            }
            return data;
        },
        containsOther: function() {
            return this.data.some(o => o.cropName == "Other");
        },
        attributeName: function() {
            if (this.assessment.viewColor == "total") {
                return { percent: "wfFraction", value: "wf"};
            }
            const color = this.assessment.viewColor;
            return { percent: `${color}WFFraction`, value: `${color}WF`};
        },
        innerRadius: function() {
            const outerRadius = 200;
            const outerSurface = Math.PI * outerRadius * outerRadius;
            const innerFraction = 1 - this.cropsWithValues.reduce((fraction, crop) => fraction += crop[this.attributeName.percent], 0);
            if (innerFraction > 0.01) {
                const innerSurface = outerSurface * innerFraction;
                return Math.sqrt(innerSurface / Math.PI);
            }
            return 0;
        }
    }
}
</script>

<style lang="scss" scoped>
.input-group {
    width: 170px;
    & * {
        font-size: inherit;
    }
}
</style>
